import { database, DB_PREFIX, invalidateCache, cacheGet, CACHE_TTL, cacheSet, isDevelopment } from "./config";
import {
    ref, remove, onValue, set, update, get,
    serverTimestamp, query, orderByChild, push, equalTo,
} from "firebase/database";


const generateProfileSlug = (nameOrUsername) => {
    const firstName = (nameOrUsername || '')
        .split(/[\s_-]/)[0]
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') || 'user';
    const num = Math.floor(1000 + Math.random() * 9000); // 4-digit number 1000–9999
    return `${firstName}-${num}`;
};

// Helper function to check if URL is a LinkedIn URL
const isLinkedInUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const lowerUrl = url.toLowerCase().trim();
    return lowerUrl.includes('linkedin.com') || lowerUrl.includes('linkedin.');
};

// Admin function to remove LinkedIn URLs from portfolio_url fields
export const cleanupLinkedInFromPortfolio = async () => {
    if (!database) {
        console.error("Firebase not initialized");
        return { success: false, error: "Firebase not initialized" };
    }

    try {
        const usersRef = ref(database, `${DB_PREFIX}users`);
        const snapshot = await get(usersRef);

        if (!snapshot.exists()) {
            return { success: true, message: "No users found", count: 0 };
        }

        const updates = {};
        const affectedUsers = [];

        snapshot.forEach((childSnapshot) => {
            const userData = childSnapshot.val();
            const userId = childSnapshot.key;

            if (userData.portfolio_url && isLinkedInUrl(userData.portfolio_url)) {
                affectedUsers.push({
                    userId,
                    email: userData.email || 'N/A',
                    name: userData.full_name || 'N/A',
                    oldPortfolioUrl: userData.portfolio_url
                });
                updates[`${DB_PREFIX}users/${userId}/portfolio_url`] = '';
            }
        });

        if (affectedUsers.length === 0) {
            return { success: true, message: "No LinkedIn URLs found in portfolio fields", count: 0 };
        }

        // Perform the updates
        await update(ref(database), updates);

        console.log(`Cleaned up LinkedIn URLs from ${affectedUsers.length} profile(s):`, affectedUsers);
        return {
            success: true,
            message: `Removed LinkedIn URLs from ${affectedUsers.length} profile(s)`,
            count: affectedUsers.length,
            affectedUsers
        };
    } catch (error) {
        console.error("Error cleaning up LinkedIn URLs:", error);
        return { success: false, error: error.message };
    }
}

export const toggleUserBlockStatus = async (userId, currentBlockedStatus) => {
    invalidateCache('candidateList');
    if (!database) {
        console.warn("Firebase not initialized - user block status not updated");
        return null;
    }

    try {
        const userRef = ref(database, `${DB_PREFIX}users/${userId}`);
        const snapshot = await new Promise((resolve, reject) => {
            onValue(userRef, resolve, reject, { onlyOnce: true });
        });

        if (!snapshot.exists()) {
            console.error("User not found");
            return null;
        }

        const userData = snapshot.val();
        await set(userRef, {
            ...userData,
            blocked: !currentBlockedStatus,
            blockedAt: !currentBlockedStatus ? serverTimestamp() : null
        });

        console.log(`User ${!currentBlockedStatus ? 'blocked' : 'unblocked'} successfully`);
        return !currentBlockedStatus;
    } catch (error) {
        console.error("Error toggling user block status:", error);
        throw error;
    }
}

export const deleteUser = async (userId) => {
    if (!database) {
        console.warn("Firebase not initialized - user not deleted");
        return;
    }

    try {
        // First, get the user data to find their email
        const userRef = ref(database, `${DB_PREFIX}users/${userId}`);
        const userSnapshot = await new Promise((resolve, reject) => {
            onValue(userRef, resolve, reject, { onlyOnce: true });
        });

        const userData = userSnapshot.val();
        const userEmail = userData?.email;

        console.log("Deleting user and all associated data:", userId, userEmail);

        // 1. Delete user's resumes (stored by email)
        if (userEmail) {
            const sanitizedEmail = userEmail.replace(/\./g, '_');
            const resumesRef = ref(database, `${DB_PREFIX}resumes/${sanitizedEmail}`);
            await remove(resumesRef);
            console.log("User resumes deleted");
        }

        // 2. Delete user's tracker items
        if (userEmail) {
            const trackerRef = ref(database, `${DB_PREFIX}trackerItems`);
            const trackerSnapshot = await new Promise((resolve, reject) => {
                onValue(trackerRef, resolve, reject, { onlyOnce: true });
            });

            const deletePromises = [];
            trackerSnapshot.forEach((childSnapshot) => {
                const item = childSnapshot.val();
                if (item.userEmail === userEmail) {
                    deletePromises.push(remove(ref(database, `${DB_PREFIX}trackerItems/${childSnapshot.key}`)));
                }
            });
            await Promise.all(deletePromises);
            invalidateCache('candidateList');
            console.log("User tracker items deleted");

        }

        // 3. Delete user's reviews
        if (userEmail) {
            const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
            const reviewsSnapshot = await new Promise((resolve, reject) => {
                onValue(reviewsRef, resolve, reject, { onlyOnce: true });
            });

            const deletePromises = [];
            reviewsSnapshot.forEach((childSnapshot) => {
                const review = childSnapshot.val();
                if (review.userEmail === userEmail) {
                    deletePromises.push(remove(ref(database, `${DB_PREFIX}reviews/${childSnapshot.key}`)));
                }
            });
            await Promise.all(deletePromises);
            console.log("User reviews deleted");
        }

        // 4. Delete user's comments on reviews
        if (userEmail) {
            const commentsRef = ref(database, `${DB_PREFIX}comments`);
            const commentsSnapshot = await new Promise((resolve, reject) => {
                onValue(commentsRef, resolve, reject, { onlyOnce: true });
            });

            const deletePromises = [];
            commentsSnapshot.forEach((reviewComments) => {
                reviewComments.forEach((commentSnapshot) => {
                    const comment = commentSnapshot.val();
                    if (comment.userEmail === userEmail) {
                        deletePromises.push(remove(ref(database, `${DB_PREFIX}comments/${reviewComments.key}/${commentSnapshot.key}`)));
                    }
                });
            });
            await Promise.all(deletePromises);
            console.log("User comments deleted");
        }

        // 5. Remove user from thumbsUpUsers in all reviews
        if (userEmail) {
            const reviewsRef = ref(database, `${DB_PREFIX}reviews`);
            const reviewsSnapshot = await new Promise((resolve, reject) => {
                onValue(reviewsRef, resolve, reject, { onlyOnce: true });
            });

            const updatePromises = [];
            reviewsSnapshot.forEach((childSnapshot) => {
                const review = childSnapshot.val();
                if (review.thumbsUpUsers && review.thumbsUpUsers.includes(userEmail)) {
                    const newThumbsUpUsers = review.thumbsUpUsers.filter(email => email !== userEmail);
                    updatePromises.push(
                        update(ref(database, `${DB_PREFIX}reviews/${childSnapshot.key}`), {
                            thumbsUpUsers: newThumbsUpUsers,
                            thumbsUpCount: Math.max(0, (review.thumbsUpCount || 0) - 1)
                        })
                    );
                }
            });
            await Promise.all(updatePromises);
            console.log("User removed from review thumbs up lists");
        }

        // 6. Delete user's chat presence from all chatrooms
        const chatsRef = ref(database, `${DB_PREFIX}chats`);
        const chatsSnapshot = await new Promise((resolve, reject) => {
            onValue(chatsRef, resolve, reject, { onlyOnce: true });
        });

        const presenceDeletePromises = [];
        chatsSnapshot.forEach((chatroomSnapshot) => {
            const presenceRef = ref(database, `${DB_PREFIX}chats/${chatroomSnapshot.key}/presence/${userId}`);
            presenceDeletePromises.push(remove(presenceRef));
        });
        await Promise.all(presenceDeletePromises);
        console.log("User presence deleted from all chatrooms");

        // 7. Delete user's deleted chat messages
        if (userEmail) {
            try {
                const deletedMsgsRef = ref(database, `${DB_PREFIX}deletedChatMessages`);
                const deletedMsgsSnapshot = await new Promise((resolve, reject) => {
                    onValue(deletedMsgsRef, resolve, reject, { onlyOnce: true });
                });

                const deletePromises = [];
                deletedMsgsSnapshot.forEach((chatroomSnapshot) => {
                    chatroomSnapshot.forEach((messageSnapshot) => {
                        const message = messageSnapshot.val();
                        if (message.deletedBy === userEmail) {
                            deletePromises.push(
                                remove(ref(database, `${DB_PREFIX}deletedChatMessages/${chatroomSnapshot.key}/${messageSnapshot.key}`))
                            );
                        }
                    });
                });
                await Promise.all(deletePromises);
                console.log("User deleted chat messages removed");
            } catch (e) {
                console.warn("Could not delete chat messages (permission denied — skipping):", e.message);
            }
        }

        // 8. Delete user's interview requests (as candidate)
        if (userEmail) {
            const requestsRef = ref(database, `${DB_PREFIX}interviewRequests`);
            const requestsSnapshot = await new Promise((resolve, reject) => {
                onValue(requestsRef, resolve, reject, { onlyOnce: true });
            });

            const deletePromises = [];
            requestsSnapshot.forEach((childSnapshot) => {
                const request = childSnapshot.val();
                if (request.candidateEmail === userEmail) {
                    deletePromises.push(remove(ref(database, `${DB_PREFIX}interviewRequests/${childSnapshot.key}`)));
                }
            });
            await Promise.all(deletePromises);
            console.log("User interview requests deleted");
        }

        // 9. Delete user's job interests
        if (userEmail) {
            try {
                const sanitizedEmail = userEmail.replace(/\./g, '_');
                const jobInterestsRef = ref(database, `${DB_PREFIX}jobInterests`);
                const jobInterestsSnapshot = await new Promise((resolve, reject) => {
                    onValue(jobInterestsRef, resolve, reject, { onlyOnce: true });
                });

                const deletePromises = [];
                jobInterestsSnapshot.forEach((jobSnapshot) => {
                    jobSnapshot.forEach((candidateSnapshot) => {
                        if (candidateSnapshot.key === sanitizedEmail) {
                            deletePromises.push(
                                remove(ref(database, `${DB_PREFIX}jobInterests/${jobSnapshot.key}/${candidateSnapshot.key}`))
                            );
                        }
                    });
                });
                await Promise.all(deletePromises);
                console.log("User job interests deleted");
            } catch (err) {
                console.warn("Could not delete job interests (non-critical):", err.message);
            }
        }

        // 10. Delete user's interest credits
        if (userEmail) {
            try {
                const sanitizedEmail = userEmail.replace(/\./g, '_');
                const creditsRef = ref(database, `${DB_PREFIX}interestCredits/${sanitizedEmail}`);
                await remove(creditsRef);
                console.log("User interest credits deleted");
            } catch (err) {
                console.warn("Could not delete interest credits (non-critical):", err.message);
            }
        }

        // 11. Delete community thread posts and replies
        if (userEmail) {
            try {
                // Delete from general thread posts
                const postsRef = ref(database, `${DB_PREFIX}threads/posts`);
                const postsSnapshot = await new Promise((resolve, reject) => {
                    onValue(postsRef, resolve, reject, { onlyOnce: true });
                });

                const deletePromises = [];
                postsSnapshot.forEach((childSnapshot) => {
                    const post = childSnapshot.val();
                    if (post.author_email === userEmail) {
                        deletePromises.push(remove(ref(database, `${DB_PREFIX}threads/posts/${childSnapshot.key}`)));
                    }
                });
                await Promise.all(deletePromises);
                console.log("User thread posts deleted");
            } catch (err) {
                console.warn("Could not delete thread posts (non-critical):", err.message);
            }

            try {
                // Delete from general thread replies
                const repliesRef = ref(database, `${DB_PREFIX}threads/replies`);
                const repliesSnapshot = await new Promise((resolve, reject) => {
                    onValue(repliesRef, resolve, reject, { onlyOnce: true });
                });

                const replyDeletePromises = [];
                repliesSnapshot.forEach((postSnapshot) => {
                    postSnapshot.forEach((replySnapshot) => {
                        const reply = replySnapshot.val();
                        if (reply.author_email === userEmail) {
                            replyDeletePromises.push(
                                remove(ref(database, `${DB_PREFIX}threads/replies/${postSnapshot.key}/${replySnapshot.key}`))
                            );
                        }
                    });
                });
                await Promise.all(replyDeletePromises);
                console.log("User thread replies deleted");
            } catch (err) {
                console.warn("Could not delete thread replies (non-critical):", err.message);
            }
        }

        // 12. Store the email in a cleanup list for Firebase Auth deletion
        if (userEmail) {
            try {
                const authCleanupRef = ref(database, `${DB_PREFIX}authAccountsToDelete/${Date.now()}`);
                await set(authCleanupRef, {
                    email: userEmail,
                    userId: userId,
                    deletedAt: new Date().toISOString(),
                    reason: 'User deleted from admin panel'
                });
                console.log("Added to Firebase Auth cleanup list:", userEmail);
            } catch (cleanupError) {
                console.warn("Could not add to auth cleanup list (non-critical):", cleanupError.message);
            }
        }

        // 13. Finally, delete the user profile
        await remove(userRef);
        console.log("User profile deleted");

        console.log("User and all associated data deleted successfully");
        return true;
    } catch (error) {
        console.error("Error deleting user:", error);
        throw error;
    }
}


export const saveUser = async (userData) => {
    if (!database) {
        console.warn("Firebase not initialized - user data saved to localStorage only");
        return null;
    }

    try {
        const usersRef = ref(database, `${DB_PREFIX}users`);
        const userEmailLower = userData.email?.toLowerCase();

        // Use indexed query instead of downloading all users
        const emailQuery = query(usersRef, orderByChild('email'), equalTo(userEmailLower));
        const snapshot = await new Promise((resolve, reject) => {
            onValue(emailQuery, resolve, reject, { onlyOnce: true });
        });

        let existingUserKey = null;
        let existingUserData = null;
        snapshot.forEach((childSnapshot) => {
            existingUserKey = childSnapshot.key;
            existingUserData = childSnapshot.val();
        });

        if (existingUserKey) {
            // User already exists, update lastLogin and any new data.
            // Backfill profile_slug if missing (existing accounts).
            const mergedData = { ...existingUserData, ...userData, lastLogin: serverTimestamp() };
            if (!mergedData.profile_slug) {
                mergedData.profile_slug = generateProfileSlug(mergedData.first_name || mergedData.full_name || mergedData.username);
            }
            const userRef = ref(database, `${DB_PREFIX}users/${existingUserKey}`);
            await set(userRef, mergedData);
            invalidateCache('user:');
            invalidateCache('candidateList');
            return existingUserKey;
        } else {
            // New user — always generate a profile_slug
            const profile_slug = userData.profile_slug || generateProfileSlug(userData.first_name || userData.full_name || userData.username);
            const newUserRef = push(usersRef);
            await set(newUserRef, {
                ...userData,
                profile_slug,
                createdAt: serverTimestamp(),
                lastLogin: serverTimestamp()
            });
            return newUserRef.key;
        }
    } catch (error) {
        console.error("Error saving user to Firebase:", error);
        throw error;
    }
}

export const updateLastLogin = async (userIdentifier) => {
    if (!database) {
        console.warn("Firebase not initialized - last login not tracked");
        return null;
    }

    try {
        const usersRef = ref(database, `${DB_PREFIX}users`);
        const identifierLower = userIdentifier?.toLowerCase();

        // Try email index first (most common case)
        let userKey = null;
        let userData = null;
        const emailQuery = query(usersRef, orderByChild('email'), equalTo(identifierLower));
        const emailSnap = await new Promise((resolve, reject) => {
            onValue(emailQuery, resolve, reject, { onlyOnce: true });
        });
        emailSnap.forEach((child) => {
            userKey = child.key;
            userData = child.val();
        });

        // If not found by email, try username index
        if (!userKey) {
            const usernameQuery = query(usersRef, orderByChild('username'), equalTo(identifierLower));
            const usernameSnap = await new Promise((resolve, reject) => {
                onValue(usernameQuery, resolve, reject, { onlyOnce: true });
            });
            usernameSnap.forEach((child) => {
                userKey = child.key;
                userData = child.val();
            });
        }

        if (userKey && userData) {
            const userRef = ref(database, `${DB_PREFIX}users/${userKey}`);
            await set(userRef, {
                ...userData,
                lastLogin: serverTimestamp()
            });
            invalidateCache('user:');
            return userKey;
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error updating last login:", error);
        throw error;
    }
}
// Get user data by email — uses indexed query + cache for speed
export const getUserByEmail = async (email) => {
    if (!database || !email) return null;

    const emailLower = email.toLowerCase();
    const cacheKey = `userByEmail:${emailLower}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) return cached;

    try {
        let userData = null;
        const currentPath = `${DB_PREFIX}users`;

        try {
            // Use orderByChild('email') + equalTo for an indexed query (downloads only matching records)
            const q = query(ref(database, currentPath), orderByChild('email'), equalTo(emailLower));
            const snapshot = await get(q);

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot) => {
                    userData = { id: childSnapshot.key, ...childSnapshot.val() };
                });
            }

            // Fallback: try case-insensitive scan if the stored email has different casing
            if (!userData) {
                const exactQ = query(ref(database, currentPath), orderByChild('email'), equalTo(email));
                const exactSnap = await get(exactQ);
                if (exactSnap.exists()) {
                    exactSnap.forEach((childSnapshot) => {
                        userData = { id: childSnapshot.key, ...childSnapshot.val() };
                    });
                }
            }
        } catch (indexError) {
            // Fallback: full scan if index not defined
            console.warn('getUserByEmail: index unavailable, using full scan, error:', indexError);
            const snapshot = await new Promise((resolve, reject) => {
                onValue(ref(database, currentPath), resolve, reject, { onlyOnce: true });
            });
            snapshot.forEach((childSnapshot) => {
                const user = childSnapshot.val();
                if (user.email?.toLowerCase() === emailLower) {
                    userData = { id: childSnapshot.key, ...user };
                }
            });
        }

        // If still not found, check alternate environment path
        if (!userData) {
            const alternatePath = isDevelopment ? 'users' : 'dev/users';
            try {
                const altQ = query(ref(database, alternatePath), orderByChild('email'), equalTo(emailLower));
                const altSnap = await get(altQ);
                if (altSnap.exists()) {
                    altSnap.forEach((childSnapshot) => {
                        userData = { id: childSnapshot.key, ...childSnapshot.val() };
                    });
                }
                if (!userData) {
                    const altQ2 = query(ref(database, alternatePath), orderByChild('email'), equalTo(email));
                    const altSnap2 = await get(altQ2);
                    if (altSnap2.exists()) {
                        altSnap2.forEach((childSnapshot) => {
                            userData = { id: childSnapshot.key, ...childSnapshot.val() };
                        });
                    }
                }
            } catch (altError) {
                // Alternate path index not available, skip
                console.error("Error getting user by alternate path:", altError);
            }
        }

        cacheSet(cacheKey, userData, CACHE_TTL.userByEmail);
        return userData;
    } catch (error) {
        console.error("Error getting user by email:", error);
        return null;
    }
}

// Get user data by username — uses indexed query + cache for speed
export const getUserByUsername = async (username) => {
    if (!database || !username) return null;

    const usernameLower = username.toLowerCase();
    const cacheKey = `userByUsername:${usernameLower}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) return cached;

    // Helper to query a path by a given field and value
    const queryByField = async (path, field, value) => {
        try {
            const q = query(ref(database, path), orderByChild(field), equalTo(value));
            const snap = await get(q);
            if (snap.exists()) {
                let result = null;
                snap.forEach((child) => { result = { id: child.key, ...child.val() }; });
                return result;
            }
        } catch (_) { console.error("Error: ", _); }
        return null;
    };

    try {
        let userData = null;
        const currentPath = `${DB_PREFIX}users`;

        // 1. Try profile_slug (new format: "shanna-4821")
        userData = await queryByField(currentPath, 'profile_slug', usernameLower);

        // 2. Try exact username match
        if (!userData) userData = await queryByField(currentPath, 'username', username);

        // 3. Try lowercase username
        if (!userData && username !== usernameLower) {
            userData = await queryByField(currentPath, 'username', usernameLower);
        }

        // 4. Full scan fallback (covers legacy accounts without indexed fields)
        if (!userData) {
            try {
                const snapshot = await new Promise((resolve, reject) => {
                    onValue(ref(database, currentPath), resolve, reject, { onlyOnce: true });
                });
                snapshot.forEach((childSnapshot) => {
                    const user = childSnapshot.val();
                    if (
                        user.profile_slug?.toLowerCase() === usernameLower ||
                        user.username?.toLowerCase() === usernameLower
                    ) {
                        userData = { id: childSnapshot.key, ...user };
                    }
                });
            } catch (error) { console.error("Error: ", error); }
        }

        // 5. Check alternate environment path if still not found
        if (!userData) {
            const alternatePath = isDevelopment ? 'users' : 'dev/users';
            userData = await queryByField(alternatePath, 'profile_slug', usernameLower);
            if (!userData) userData = await queryByField(alternatePath, 'username', username);
            if (!userData && username !== usernameLower) {
                userData = await queryByField(alternatePath, 'username', usernameLower);
            }
        }

        cacheSet(cacheKey, userData, CACHE_TTL.userByUsername);
        return userData;
    } catch (error) {
        console.error("Error getting user by username:", error);
        return null;
    }
}

// Update user profile data
export const updateUserProfile = async (email, profileData) => {
    if (!database) {
        console.warn("Firebase not initialized - profile not updated");
        return null;
    }

    // Invalidate cache for this user
    if (email) {
        invalidateCache(`userByEmail:${email.toLowerCase()}`);
        if (profileData.username) invalidateCache(`userByUsername:`);
    }

    try {
        invalidateCache('candidateList');
        // Use indexed query to find user by email (much faster than full scan)
        const currentPath = `${DB_PREFIX}users`;
        const q = query(ref(database, currentPath), orderByChild('email'), equalTo(email.toLowerCase()));
        let snapshot = await get(q);

        let userKey = null;
        let existingData = null;
        let foundPath = currentPath;

        if (snapshot.exists()) {
            snapshot.forEach((childSnapshot) => {
                userKey = childSnapshot.key;
                existingData = childSnapshot.val();
            });
        }

        // Also try exact case
        if (!userKey && email !== email.toLowerCase()) {
            const q2 = query(ref(database, currentPath), orderByChild('email'), equalTo(email));
            const snap2 = await get(q2);
            if (snap2.exists()) {
                snap2.forEach((childSnapshot) => {
                    userKey = childSnapshot.key;
                    existingData = childSnapshot.val();
                });
            }
        }

        // If not found in current path, try alternate path
        if (!userKey) {
            const alternatePath = isDevelopment ? 'users' : 'dev/users';
            const altQ = query(ref(database, alternatePath), orderByChild('email'), equalTo(email.toLowerCase()));
            const altSnap = await get(altQ);
            if (altSnap.exists()) {
                altSnap.forEach((childSnapshot) => {
                    userKey = childSnapshot.key;
                    existingData = childSnapshot.val();
                    foundPath = alternatePath;
                });
            }
        }

        if (userKey) {
            const userRef = ref(database, `${foundPath}/${userKey}`);
            // Enforce max 5 specializations/subcategories (only if subcategories is present in the update)
            const sanitizedProfileData = {
                ...profileData,
                ...(profileData.subcategories !== undefined && {
                    subcategories: profileData.subcategories.slice(0, 5)
                })
            };
            const mergedData = {
                ...existingData,
                ...sanitizedProfileData,
                updatedAt: serverTimestamp()
            };
            console.log("updateUserProfile - Writing to:", `${foundPath}/${userKey}`, "bio length:", mergedData.bio?.length || 0);
            await set(userRef, mergedData);
            console.log("updateUserProfile - Write successful");
            return userKey;
        } else {
            // User not found - use saveUser to ensure no duplicates are created
            // saveUser has duplicate checking built in
            console.log("updateUserProfile - User not found, using saveUser to create record for:", email);
            const result = await saveUser({
                email: email,
                ...profileData
            });
            console.log("updateUserProfile - User created via saveUser");
            return result;
        }
    } catch (error) {
        console.error("Error updating user profile:", error);
        throw error;
    }
}

export const updateEmailPreferences = async (email, preferences) => {
    if (!database) {
        console.warn("Firebase not initialized - email preferences not updated");
        return null;
    }

    try {
        const currentPath = `${DB_PREFIX}users`;
        const usersRef = ref(database, currentPath);
        const snapshot = await new Promise((resolve, reject) => {
            onValue(usersRef, resolve, reject, { onlyOnce: true });
        });

        let userKey = null;
        let foundPath = currentPath;
        const emailLower = email?.toLowerCase();

        snapshot.forEach((childSnapshot) => {
            const userData = childSnapshot.val();
            if (userData.email?.toLowerCase() === emailLower) {
                userKey = childSnapshot.key;
            }
        });

        if (!userKey) {
            const alternatePath = isDevelopment ? `users` : `dev/users`;
            const altRef = ref(database, alternatePath);
            const altSnapshot = await new Promise((resolve, reject) => {
                onValue(altRef, resolve, reject, { onlyOnce: true });
            });

            altSnapshot.forEach((childSnapshot) => {
                const userData = childSnapshot.val();
                if (userData.email?.toLowerCase() === emailLower) {
                    userKey = childSnapshot.key;
                    foundPath = alternatePath;
                }
            });
        }

        if (userKey) {
            const prefsRef = ref(database, `${foundPath}/${userKey}/email_preferences`);
            await set(prefsRef, preferences);
            return userKey;
        }

        return null;
    } catch (error) {
        console.error("Error updating email preferences:", error);
        throw error;
    }
}

export const subscribeToUsers = (callback) => {
    const cached = cacheGet('candidateList');
    if (cached) {
        console.log('[subscribeToUsers] Returned users from cache, count:', cached.length);
        callback(cached);
        return () => { }; // Return early if using cache - don't set up listener
    }
    if (!database) {
        console.error("Firebase not initialized");
        callback([]);
        return () => { };
    }

    try {
        // Only subscribe to current environment path (prod shows prod users, dev shows dev users)
        const path = `${DB_PREFIX}users`;
        console.log(`[subscribeToUsers] Subscribing to path: ${path}, isDevelopment: ${isDevelopment}`);
        const usersRef = ref(database, path);
        const unsubscribe = onValue(usersRef, (snapshot) => {
            const users = [];
            snapshot.forEach((childSnapshot) => {
                const userData = childSnapshot.val();
                users.push({
                    id: childSnapshot.key,
                    ...userData
                });
                // Debug: Log each user with their key fields
                console.log(`[subscribeToUsers] User: ${childSnapshot.key} | name: ${userData.full_name || userData.username} | allow_contact: ${userData.allow_contact_by_hiring_managers} (type: ${typeof userData.allow_contact_by_hiring_managers})`);
            });
            console.log(`[subscribeToUsers] Found ${users.length} users at path: ${path}`);
            // Sort by creation date (newest first)
            users.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
            cacheSet('candidateList', users, CACHE_TTL.candidateList);
            callback(users);
        }, (error) => {
            console.error("Error subscribing to users:", error);
            callback([]);
        });
        return unsubscribe;
    } catch (error) {
        console.error("Error setting up user subscription:", error);
        callback([]);
        return () => { };
    }
}

// Subscribes to onboarding reminder email send history, keyed by sanitized candidate email.
// Each entry looks like { trigger1: <timestamp>, trigger2: <timestamp>, trigger3: <timestamp> }.
export const subscribeToOnboardingEmailsSent = (callback) => {
    if (!database) {
        console.error("Firebase not initialized");
        callback({});
        return () => { };
    }

    try {
        const path = `${DB_PREFIX}onboardingEmailsSent`;
        const onboardingEmailsSentRef = ref(database, path);
        const unsubscribe = onValue(onboardingEmailsSentRef, (snapshot) => {
            callback(snapshot.val() || {});
        }, (error) => {
            console.error("Error subscribing to onboarding emails sent:", error);
            callback({});
        });
        return unsubscribe;
    } catch (error) {
        console.error("Error setting up onboarding emails sent subscription:", error);
        callback({});
        return () => { };
    }
}


// Clean up duplicate users - keeps the oldest entry for each email
export const cleanupDuplicateUsers = async () => {
    if (!database) {
        console.warn("Firebase not initialized");
        return { removed: 0, kept: 0 };
    }

    try {
        const usersRef = ref(database, `${DB_PREFIX}users`);
        const snapshot = await new Promise((resolve, reject) => {
            onValue(usersRef, resolve, reject, { onlyOnce: true });
        });

        // Group users by email (case-insensitive)
        const usersByEmail = {};
        snapshot.forEach((childSnapshot) => {
            const user = childSnapshot.val();
            const email = user.email?.toLowerCase();
            if (!email) return;

            if (!usersByEmail[email]) {
                usersByEmail[email] = [];
            }
            usersByEmail[email].push({
                key: childSnapshot.key,
                ...user
            });
        });

        let removedCount = 0;
        let keptCount = 0;

        // For each email with duplicates, merge data and keep one record
        for (const email in usersByEmail) {
            const users = usersByEmail[email];
            if (users.length > 1) {
                console.log(`Found ${users.length} duplicates for: ${email}`);

                // Sort by createdAt (oldest first) - oldest becomes the base
                users.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

                // Merge all user data, with newer data taking precedence for non-empty values
                const mergedData = {};
                for (const user of users) {
                    for (const key in user) {
                        if (key === 'key') continue; // Skip the Firebase key
                        const value = user[key];
                        // Only overwrite if the new value is truthy (not empty/null/undefined)
                        // Arrays: prefer non-empty arrays
                        // Strings: prefer non-empty strings
                        // Objects: merge recursively
                        if (Array.isArray(value)) {
                            if (value.length > 0) mergedData[key] = value;
                        } else if (typeof value === 'object' && value !== null) {
                            mergedData[key] = { ...(mergedData[key] || {}), ...value };
                        } else if (value !== null && value !== undefined && value !== '') {
                            mergedData[key] = value;
                        }
                    }
                }

                // Use the oldest record's key
                const keepKey = users[0].key;

                // Get most recent timestamps
                mergedData.lastLogin = users.reduce((latest, u) =>
                    (u.lastLogin || 0) > (latest || 0) ? u.lastLogin : latest, 0);
                mergedData.updatedAt = serverTimestamp();

                // If any duplicate had onboardingComplete = true, keep it true
                const anyOnboardingComplete = users.some(u => u.onboardingComplete === true);
                if (anyOnboardingComplete) {
                    mergedData.onboardingComplete = true;
                }

                // Update the kept record with merged data
                const keepRef = ref(database, `${DB_PREFIX}users/${keepKey}`);
                await set(keepRef, mergedData);
                console.log(`Merged and kept user: ${email} (key: ${keepKey})`);
                keptCount++;

                // Remove the duplicate records
                for (let i = 1; i < users.length; i++) {
                    const userRef = ref(database, `${DB_PREFIX}users/${users[i].key}`);
                    await remove(userRef);
                    removedCount++;
                    console.log(`Removed duplicate: ${email} (key: ${users[i].key})`);
                }
            } else {
                keptCount++;
            }
        }

        console.log(`Cleanup complete: ${removedCount} duplicates removed, ${keptCount} unique users kept`);
        return { removed: removedCount, kept: keptCount };
    } catch (error) {
        console.error("Error cleaning up duplicate users:", error);
        throw error;
    }
};

// Get all users (only from current environment - prod shows prod users, dev shows dev users)
// Paginated getAllUsers (limit, startAfterKey)
export const getAllUsers = async (limit = 50, startAfterKey = null) => {
    if (!database) {
        console.warn("Firebase not initialized");
        return [];
    }
    try {
        const users = [];
        const currentPath = `${DB_PREFIX}users`;
        let queryRef = ref(database, currentPath);
        // Firebase Realtime DB does not support offset, so we use startAfterKey for pagination
        if (startAfterKey) {
            // Use orderByKey and startAfter for pagination
            const { orderByKey, startAfter, limitToFirst } = await import("firebase/database");
            queryRef = query(queryRef, orderByKey(), startAfter(startAfterKey), limitToFirst(limit));
        } else {
            const { orderByKey, limitToFirst } = await import("firebase/database");
            queryRef = query(queryRef, orderByKey(), limitToFirst(limit));
        }
        const snap = await get(queryRef);
        snap.forEach((childSnapshot) => {
            const userData = childSnapshot.val();
            if (userData.email) {
                users.push({
                    id: childSnapshot.key,
                    ...userData
                });
            }
        });
        return users;
    } catch (error) {
        console.error("Error getting all users:", error);
        return [];
    }
};

// Clean up user skills - trim to max 10 skills for all users
export const cleanupUserSkills = async () => {
    if (!database) {
        console.warn("Firebase not initialized");
        return { usersUpdated: 0, totalSkillsRemoved: 0 };
    }

    try {
        const users = await getAllUsers();
        let usersUpdated = 0;
        let totalSkillsRemoved = 0;

        for (const user of users) {
            // Check if user has tags array with more than 10 items
            if (user.tags && Array.isArray(user.tags) && user.tags.length > 10) {
                const originalCount = user.tags.length;
                const trimmedTags = user.tags.slice(0, 10);
                const skillsRemoved = originalCount - 10;

                console.log(`Cleaning up user: ${user.email}`);
                console.log(`  - Original skills: ${originalCount}`);
                console.log(`  - Skills removed: ${skillsRemoved}`);

                // Update the user's tags in the database
                await updateUserProfile(user.email, { tags: trimmedTags });

                usersUpdated++;
                totalSkillsRemoved += skillsRemoved;
            }
        }

        console.log('-----------------------------------');
        console.log(`Cleanup complete! Users updated: ${usersUpdated}, Total skills removed: ${totalSkillsRemoved}`);
        return { usersUpdated, totalSkillsRemoved };
    } catch (error) {
        console.error("Error cleaning up user skills:", error);
        throw error;
    }
}
